import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { toast } from 'react-toastify';
import { 
  XMarkIcon,
  BuildingStorefrontIcon,
  IdentificationIcon,
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  PlusIcon,
  LockClosedIcon,
  CubeIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const AddAutoShopModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    brand_name: '',
    tax_identifier: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    packageId: ''
  });

  useEffect(() => {
    if (isOpen) {
      getApiClient().get('/auto-shop/billing/package')
        .then(res => setPackages(res.data))
        .catch(() => toast.error('Failed to load packages'));
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      brand_name: '',
      tax_identifier: '',
      email: '',
      phone: '',
      address: '',
      password: '',
      packageId: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['brand_name', 'tax_identifier', 'email', 'phone', 'address', 'password', 'packageId'];
    
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        toast.error(`Please fill in the ${field.replace('_', ' ')} field`);
        return false;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate tax identifier format (Romanian CUI)
    const cuiRegex = /^(RO)?[0-9]{2,10}$/;
    if (!cuiRegex.test(formData.tax_identifier)) {
      toast.error('Please enter a valid tax identifier (CUI)');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const client = getApiClient();
      await client.post('/admin/shop/add', formData);

      toast.success('Auto shop added successfully!');
      resetForm();
      onSuccess(); // Refresh the shops list
      onClose();
      
    } catch (error) {
      console.error('Error adding auto shop:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add auto shop';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-3/5 lg:w-1/2 max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <PlusIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">
              {t('addNewAutoShop')}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Brand Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('brandName')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                required
                value={formData.brand_name}
                onChange={(e) => handleInputChange('brand_name', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="e.g., Auto Service Pro"
                disabled={loading}
              />
            </div>
          </div>

          {/* Tax Identifier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('taxIdentifier')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IdentificationIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                required
                value={formData.tax_identifier}
                onChange={(e) => handleInputChange('tax_identifier', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="e.g., RO12345678"
                disabled={loading}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t('businessDataWillBeFetched')}
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('email')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="e.g., contact@autoservicepro.ro"
                disabled={loading}
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('phone')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="e.g., +40721234567"
                disabled={loading}
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('address')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="e.g., Str. Principala nr. 123, Bucuresti"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('password')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockClosedIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Initial account password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Package */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('package')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CubeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <select
                required
                value={formData.packageId}
                onChange={(e) => handleInputChange('packageId', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                disabled={loading || packages.length === 0}
              >
                <option value="">Select a package</option>
                {packages.map(pkg => (
                  <option key={pkg.packageId} value={pkg.packageId}>
                    {pkg.name} — {pkg.price} RON
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Info box */}          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <IdentificationIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('automaticDataFetching')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t('businessDataWillBeFetched')}:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>{t('officialBusinessName')}</li>
                    <li>{t('registrationDetails')}</li>
                    <li>{t('vatStatus')}</li>
                    <li>{t('otherRelevantBusinessData')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              disabled={loading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('addingAutoShop')}
                </div>
              ) : (
                t('addAutoShop')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAutoShopModal;